<template>
  <div class="select-wrapper" ref="wrapper" :style="_wrapperStyle">
    <input
      class="select"
      ref="select"
      type="text"
      :style="_innerStyle"
      :readonly="props.readonly"
      :disabled="props.disabled"
      :placeholder="props.placeholder"
      :onfocus="handleFocus"
      @blur="handleBlur"
      @change="handleChange"
    />
    <slot name="suffix"></slot>
    <div :class="`clear iconfont ${iconType}`" @mousedown.prevent="handleClear"></div>
    <selectDropdown
      ref="dropdown"
      v-show="showDropMenu"
      :width="props.width"
      :dropWidth="props.dropWidth"
      :selectValue="props.modelValue"
      :dropBackground="props.dropBackground"
      :dropTop="props.height || 0"
      @optionClick="handleClick"
      @optionUpdate="handleOptionUpdate"
    >
      <slot></slot>
    </selectDropdown>
  </div>
</template>

<script setup lang="ts">
import {
  defineProps,
  defineEmits,
  shallowRef,
  onMounted,
  ref,
  computed,
  watch,
} from "vue";
import { selectProps, selectEmit } from "./select";
import selectDropdown from "./select-dropdown.vue";

const showDropMenu = ref(false);

const props = defineProps(selectProps);
const emits = defineEmits(selectEmit);

const select = shallowRef<HTMLInputElement>();
const dropdown = shallowRef<any>();
const wrapper = shallowRef<HTMLDivElement>();
const _dropdownRef = computed(() => dropdown.value);
const _selectRef = computed(() => select.value);
const _wrapperRef = computed(() => wrapper.value);

watch(
  () => props.modelValue,
  () => {
    calcSelectedDisplay(props.modelValue);
  }
);

watch(
  () => props.disabled,
  () => {
    if (props.disabled) {
      setDisabled();
    } else {
      rmDisabled();
    }
  }
);

const _wrapperStyle = computed(() => {
  let style = {};
  if (props.width) {
    style = Object.assign(style, { width: props.width + "px" });
  }
  if (props.height) {
    style = Object.assign(style, { height: props.height + "px" });
  }
  style = Object.assign(style, props.selectWrapperStyle);
  return style;
});
const _innerStyle = computed(() => {
  let style = {};
  if (props.width) {
    style = Object.assign(style, { width: "100%" });
  }
  if (props.height) {
    style = Object.assign(style, { height: props.height - 2 + "px" });
  }
  style = Object.assign(style, props.selectInnterStyle);
  return style;
});

// 计算选择后在Input中展示的内容
const calcSelectedDisplay = (value: string) => {
  select.value!.value = props.format
    ? props.format(value)
    : _dropdownRef.value!.getOptionLabel(props.modelValue) || "";
  // 计算宽度替换文字成...
  if (props.suffixWidthOffset
    && (calcFontWidth(select.value!.value) > ((select.value?.offsetWidth || 0) - props.suffixWidthOffset))
  ) {
    while ((calcFontWidth(select.value!.value) > ((select.value?.offsetWidth || 0) - props.suffixWidthOffset))) {
      const tmp = select.value!.value;
      select.value!.value = tmp.substring(0, tmp.length - 4) + '...'
    }
  }
};

const isFocus = ref<boolean>();
const iconType = ref<string>("icon-xialazhankaiicon");
const handleFocus = () => {
  _dropdownRef.value.setOptionFontColor();
  isFocus.value = true;
  // 仅在有值且允许删除的时候显示删除键
  if (props.modelValue && props.allowClear) {
    iconType.value = "icon-guanbiicon";
  } else {
    iconType.value = "icon-shouqizhankaiicon";
  }
  showDropMenu.value = true;
};

const handleClear = () => {
  // 根据不同的箭头种类处理，在删除的时候清空，在其他的时候收起。
  if (iconType.value.includes("guanbiicon")) {
    iconType.value = "icon-shouqizhankaiicon";
    _dropdownRef.value.removeOptionFontColor();
    emits("update:modelValue", "");
    select.value!.value = "";
    emits("clear");
    emits("change", "");
  } else if (iconType.value.includes("shouqizhankaiicon")) {
    // 模拟blur事件
    iconType.value = "icon-xialazhankaiicon";
    showDropMenu.value = false;
    _selectRef.value?.blur();
  } else if (iconType.value.includes("xialazhankaiicon") && !props.disabled) {
    // 下拉箭头时，模拟focus in且展示下拉框
    _selectRef.value?.focus();
    showDropMenu.value = true;
  }
};

const handleChange = () => {
  emits("update:modelValue", props.modelValue);
  emits("change", props.modelValue);
};

const handleBlur = () => {
  isFocus.value = false;
  // focus out重置交互
  iconType.value = "icon-xialazhankaiicon";
  // 事件重叠，将收起事件后移
  setTimeout(() => {
    showDropMenu.value = false;
  }, 100);
};

const handleClick = (value: { value: string; label: string }) => {
  emits("update:modelValue", value.value);
  // select的input中展示样式，如果format有，那么通过format匹配
  calcSelectedDisplay(value.value);
  showDropMenu.value = false;
  emits("change", value.value);
};

const setDisabled = () => {
  const _classWrapper = _wrapperRef.value?.getAttribute("class") || "";
  _wrapperRef.value?.setAttribute("class", _classWrapper + " disabled");
};

const rmDisabled = () => {
  const _classWrapper = _wrapperRef.value?.getAttribute("class") || "";
  _wrapperRef.value?.setAttribute("class", _classWrapper.replace("disabled", ""));
};

const handleOptionUpdate = () => {
  // 更新input数据
  calcSelectedDisplay(props.modelValue);
};

const calcFontWidth = (text: string) => {
  const font = "14px arial"
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')
  if (context) {
    context.font = font;
    const { width } = context.measureText(text);
    return width;
  }
  return 0
}

onMounted(() => {
  if (props.defalutValue && !props.modelValue) {
    emits("update:modelValue", props.defalutValue);
    calcSelectedDisplay(props.defalutValue);
  } else if (props.modelValue) {
    emits("update:modelValue", props.modelValue);
    calcSelectedDisplay(props.modelValue);
  } else {
    select.value!.value = "";
  }
  if (props.disabled) {
    setDisabled();
  }
});
</script>

<style lang="less" scoped>
.select-wrapper {
  width: 100%;
  border: 1px solid rgba(238, 238, 238, 1);
  display: inline-flex;
  box-sizing: border-box;
  padding-right: 12px;
  padding-left: 12px;
  font-size: 14px;
  cursor: text;
  transition: color 0.1s cubic-bezier(0, 0, 1, 1),
    border-color 0.1s cubic-bezier(0, 0, 1, 1),
    background-color 0.1s cubic-bezier(0, 0, 1, 1);
}

.select-wrapper:focus-within {
  background-color: #fff;
  border-color: #A9823E;
  box-shadow: 0 0 0 0 rgb(190, 218, 255);
}

.select {
  height: 50px;
  border: none;
  font-size: 14px;
  width: 100%;
  text-overflow: ellipsis;
}

.select:focus {
  outline: none;
}
.iconfont {
  align-self: center;
}

.clear {
  margin-left: 1px;
  align-self: center;
  cursor: pointer;
  margin-top: 2px;
}

input:disabled {
  background-color: transparent;
}

.disabled {
  background-color: rgba(239, 239, 239, 0.3);
}
</style>
